import React, { useEffect } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import { Row, Col, Button, Spinner } from 'react-bootstrap';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';

import { FormikRangePicker, InputSelect } from '../../../components';
import { workdaysBetweenHolidayRequest } from '../../../requests/holidays';
import { indexSelectEmployeesRequest } from '../../../requests/employees';
import '../../../services/yupCustomMethods';

const OvertimeCompensationForm = props => {
  const {
    errors,
    isSubmitting,
    setFieldValue,
    touched,
    values,
    availableDays,
    overtimeCompensation,
    rrhh,
    dailyWorkingHours: propsDailyWorkingHours,
    onEmployeeSelect
  } = props;

  const dispatch = useDispatch();

  const { startDate, endDate } = values.overtimeCompensationRequest;

  const fetchEmployees = (inputValue, callback) => {
    indexSelectEmployeesRequest({
      dispatch,
      params: { active: true, filter_name: inputValue, paginate: false },
      successCallback: response => callback(response.data.data)
    });
  };

  const dailyWorkingHours =
    propsDailyWorkingHours ||
    availableDays?.dailyWorkingHours ||
    (availableDays?.days > 0 ? availableDays.hours / availableDays.days : 1);

  const noAvailableDays = !availableDays?.days && !availableDays?.hours;
  const insufficientHours = availableDays?.days === 0 && availableDays?.hours < dailyWorkingHours;
  const hasInsufficientHours = noAvailableDays || insufficientHours;

  useEffect(() => {
    if (startDate && endDate) {
      workdaysBetweenHolidayRequest({
        dispatch,
        params: {
          start_date: startDate,
          end_date: endDate,
          include_all_days: true
        },
        successCallback: response => {
          const daysTaken = response.data;
          setFieldValue('overtimeCompensationRequest.daysTaken', daysTaken > 0 ? daysTaken : 0);

          if (availableDays && daysTaken > availableDays.days) {
            setFieldValue(
              'errors.overtimeCompensationRequest.daysTaken',
              'El total de días solicitados excede los días disponibles'
            );
          }
        },
        errorCallback: error => {
          console.error('Error calculando días hábiles:', error);
          setFieldValue('overtimeCompensationRequest.daysTaken', 0);
        }
      });
    } else {
      setFieldValue('overtimeCompensationRequest.daysTaken', 0);
    }
  }, [startDate, endDate, setFieldValue, dispatch, availableDays]);

  const exceedsDaysAvailable = values.overtimeCompensationRequest.daysTaken > (availableDays?.days || 0);

  return (
    <Form>
      {isSubmitting && <Spinner animation="border" variant="primary" className="spinner-modal" />}

      <Row className="mt-3">
        <Col md={6}>
          <div className="mb-3">
            <strong>Días disponibles:</strong> {availableDays?.days || 0} días, {availableDays?.hours?.toFixed(1) || 0}{' '}
            horas
            {hasInsufficientHours && (
              <div className="small mt-1 text-danger">
                No tienes suficientes horas extras acumuladas para realizar una solicitud
              </div>
            )}
          </div>
        </Col>
      </Row>

      <Row>
        {rrhh && (
          <Col md={12}>
            <Field name="overtimeCompensationRequest[employeeId]">
              {({ field }) => (
                <InputSelect
                  {...field}
                  abbr
                  label="Trabajador"
                  placeholder="Seleccionar Trabajador"
                  values={values}
                  request={fetchEmployees}
                  model={[overtimeCompensation, 'employee']}
                  onChange={data => {
                    const employeeId = data ? data.value : '';
                    setFieldValue(field.name, employeeId);
                    onEmployeeSelect(employeeId);
                  }}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
        )}
        <Col md={10}>
          <Field name="rangeDate">
            {({ field, form }) => (
              <FormikRangePicker
                name={field.name}
                abbr
                minCurrentDate={false}
                startDateName="overtimeCompensationRequest[startDate]"
                endDateName="overtimeCompensationRequest[endDate]"
                startDate={getIn(form.values, 'overtimeCompensationRequest.startDate')}
                endDate={getIn(form.values, 'overtimeCompensationRequest.endDate')}
                onChange={(start, end) => {
                  form.setFieldValue('overtimeCompensationRequest[startDate]', start);
                  form.setFieldValue('overtimeCompensationRequest[endDate]', end);
                }}
                showClearDates
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col md={2}>
          <Field name="overtimeCompensationRequest[daysTaken]">
            {({ field, form }) => (
              <div className="form-group mb-4">
                <strong>Total Días</strong>
                <input
                  id="daysTaken"
                  type="number"
                  className={`form-control ${
                    getIn(form.errors, 'overtimeCompensationRequest.daysTaken') || exceedsDaysAvailable
                      ? 'is-invalid'
                      : ''
                  }`}
                  disabled
                  value={values.overtimeCompensationRequest.daysTaken || 0}
                  {...field}
                />
                {(getIn(form.errors, 'overtimeCompensationRequest.daysTaken') || exceedsDaysAvailable) && (
                  <div className="invalid-feedback">
                    {getIn(form.errors, 'overtimeCompensationRequest.daysTaken') ||
                      'El total de días solicitados excede los días disponibles'}
                  </div>
                )}
              </div>
            )}
          </Field>
        </Col>
      </Row>

      <Row className="d-flex justify-content-end mt-4">
        <Col md={4}>
          <Button
            type="submit"
            variant="primary"
            block
            disabled={isSubmitting || Object.keys(errors).length > 0 || hasInsufficientHours || exceedsDaysAvailable}
          >
            Solicitar
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const setInitialValues = props => ({
  overtimeCompensationRequest: {
    employeeId: props.overtimeCompensation?.employeeId || '',
    startDate: '',
    endDate: '',
    daysTaken: 0
  },
  rangeDate: []
});

const validationSchema = Yup.object().shape({
  rangeDate: Yup.array().rangedate(true),
  overtimeCompensationRequest: Yup.object().shape({
    employeeId: Yup.string().when('$rrhh', {
      is: true,
      then: Yup.string().required('Debes seleccionar un trabajador'),
      otherwise: Yup.string()
    }),
    startDate: Yup.date().formatdate(),
    endDate: Yup.date().formatdate(),
    daysTaken: Yup.number().test(
      'is-valid-total-days',
      'El total de días solicitados excede los días disponibles',
      function validateTotalDays(value) {
        const { availableDays } = this.options.context || {};
        if (!availableDays) return true;

        if (value > availableDays.days) {
          return false;
        }

        return true;
      }
    )
  })
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest, availableDays, dailyWorkingHours: propsDailyWorkingHours } = props;

  const daysTaken = values.overtimeCompensationRequest.daysTaken || 0;

  const dailyWorkingHours =
    propsDailyWorkingHours || (availableDays?.days > 0 ? availableDays.hours / availableDays.days : 0);

  const totalAvailableHours = (availableDays?.days || 0) * dailyWorkingHours + (availableDays?.hours || 0);
  const requestedHours = daysTaken * dailyWorkingHours;

  if (totalAvailableHours < requestedHours) {
    setSubmitting(false);
    return;
  }

  setSubmitting(true);
  formRequest(values.overtimeCompensationRequest, setSubmitting);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  mapPropsToContext: props => ({
    rrhh: props.rrhh,
    availableDays: props.availableDays,
    dailyWorkingHours: props.dailyWorkingHours
  })
})(OvertimeCompensationForm);
